/**
 * Utility functions for immutable link detection and protection in the email editor.
 * These functions help prevent accidental deletion or modification of the {{ invite_url }} link.
 */

import {
  ContentBlock,
  ContentState,
  EditorState,
  Modifier,
  SelectionState,
} from "draft-js";

import { IMMUTABLE_LINK_URL } from "./constants";

/**
 * Checks if a URL matches the immutable link pattern.
 * Matches exact "{{ invite_url }}" or URLs containing it (possibly URL-encoded).
 */
function isImmutableLinkUrl(url: string): boolean {
  if (!url) return false;
  // Exact match
  if (url === IMMUTABLE_LINK_URL) return true;
  // Contains the template variable (possibly URL-encoded)
  try {
    const decoded = decodeURIComponent(url);
    return decoded.includes(IMMUTABLE_LINK_URL);
  } catch {
    // Malformed URL encoding - check the raw URL as fallback
    return url.includes(IMMUTABLE_LINK_URL);
  }
}

/**
 * Finds the immutable link ({{ invite_url }}) in a content block by scanning for LINK entities.
 *
 * @param block - The Draft.js content block to search
 * @param contentState - The content state containing entity data
 * @returns Object with start/end offsets if found, null otherwise
 */
export function findImmutableLinkInBlock(
  block: ContentBlock,
  contentState: ContentState
): { start: number; end: number } | null {
  const blockLength = block.getLength();

  // Scan through all characters in the block looking for LINK entities
  for (let i = 0; i < blockLength; i++) {
    const entityKey = block.getEntityAt(i);
    if (entityKey) {
      const entity = contentState.getEntity(entityKey);
      if (entity.getType() === "LINK") {
        const data = entity.getData();
        // Check if this link has the immutable link URL (exact or contained)
        if (isImmutableLinkUrl(data.url)) {
          // Found the start of the immutable link, now find its end
          let end = i;
          while (end < blockLength && block.getEntityAt(end) === entityKey) {
            end += 1;
          }
          return { start: i, end };
        }
      }
    }
  }
  return null;
}

/**
 * Checks if the editor state contains the immutable link anywhere.
 *
 * @param state - The Draft.js editor state to check
 * @returns true if the immutable link exists in any block
 */
export function hasImmutableLink(state: EditorState): boolean {
  const contentState = state.getCurrentContent();
  const blocks = contentState.getBlocksAsArray();

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    if (findImmutableLinkInBlock(block, contentState)) {
      return true;
    }
  }
  return false;
}

/**
 * Checks if the current selection overlaps with the immutable link.
 * Handles multi-block selections by iterating through all blocks in the selection range.
 *
 * @param state - The Draft.js editor state to check
 * @returns true if the selection includes any part of the immutable link
 */
/**
 * Checks if the cursor is positioned immediately after the immutable link.
 * Used to block backspace from deleting into the link character by character.
 *
 * @param state - The Draft.js editor state to check
 * @returns true if cursor is at the end position of the immutable link
 */
export function cursorIsAtImmutableLinkEnd(state: EditorState): boolean {
  const selection = state.getSelection();

  // Only applies to collapsed selections (cursor, not range)
  if (!selection.isCollapsed()) {
    return false;
  }

  const contentState = state.getCurrentContent();
  const cursorKey = selection.getStartKey();
  const cursorOffset = selection.getStartOffset();
  const block = contentState.getBlockForKey(cursorKey);

  const linkRange = findImmutableLinkInBlock(block, contentState);
  if (!linkRange) {
    return false;
  }

  // Check if cursor is exactly at the end of the immutable link
  return cursorOffset === linkRange.end;
}

/**
 * Checks if the cursor is positioned at the start of the immutable link.
 * Used to block delete from deleting into the link character by character.
 *
 * @param state - The Draft.js editor state to check
 * @returns true if cursor is at the start position of the immutable link
 */
export function cursorIsAtImmutableLinkStart(state: EditorState): boolean {
  const selection = state.getSelection();

  // Only applies to collapsed selections (cursor, not range)
  if (!selection.isCollapsed()) {
    return false;
  }

  const contentState = state.getCurrentContent();
  const cursorKey = selection.getStartKey();
  const cursorOffset = selection.getStartOffset();
  const block = contentState.getBlockForKey(cursorKey);

  const linkRange = findImmutableLinkInBlock(block, contentState);
  if (!linkRange) {
    return false;
  }

  // Check if cursor is exactly at the start of the immutable link
  return cursorOffset === linkRange.start;
}

export function selectionIncludesImmutableLink(state: EditorState): boolean {
  const selection = state.getSelection();
  const contentState = state.getCurrentContent();
  const startKey = selection.getStartKey();
  const endKey = selection.getEndKey();
  const startOffset = selection.getStartOffset();
  const endOffset = selection.getEndOffset();

  // Iterate through all blocks in the selection range
  const blockMap = contentState.getBlockMap();
  let inSelection = false;
  let foundOverlap = false;

  blockMap.forEach((block, blockKey) => {
    if (foundOverlap || !block || !blockKey) return;

    if (blockKey === startKey) {
      inSelection = true;
    }

    if (inSelection) {
      const linkRange = findImmutableLinkInBlock(block, contentState);
      if (linkRange) {
        const { start: linkStart, end: linkEnd } = linkRange;

        // Determine the effective selection range for this block
        const blockStart = blockKey === startKey ? startOffset : 0;
        const blockEnd = blockKey === endKey ? endOffset : block.getLength();

        // Check if selection overlaps with immutable link using half-open interval semantics
        // This allows typing at positions immediately before (linkStart) or after (linkEnd) the link
        if (blockStart < linkEnd && blockEnd > linkStart) {
          foundOverlap = true;
        }
      }
    }

    if (blockKey === endKey) {
      inSelection = false;
    }
  });

  return foundOverlap;
}

/**
 * Finds ALL immutable links in the editor state.
 * Returns array of { blockKey, start, end } for each found.
 */
export function findAllImmutableLinks(
  state: EditorState
): Array<{ blockKey: string; start: number; end: number }> {
  const contentState = state.getCurrentContent();
  const blocks = contentState.getBlocksAsArray();
  const results: Array<{ blockKey: string; start: number; end: number }> = [];

  blocks.forEach((block) => {
    const blockKey = block.getKey();
    const blockLength = block.getLength();
    const seenEntityKeys = new Set<string>();

    for (let i = 0; i < blockLength; i++) {
      const entityKey = block.getEntityAt(i);
      if (entityKey && !seenEntityKeys.has(entityKey)) {
        seenEntityKeys.add(entityKey);
        const entity = contentState.getEntity(entityKey);
        if (entity.getType() === "LINK") {
          const data = entity.getData();
          if (isImmutableLinkUrl(data.url)) {
            // Find the end of this entity
            let end = i;
            while (end < blockLength && block.getEntityAt(end) === entityKey) {
              end += 1;
            }
            results.push({ blockKey, start: i, end });
          }
        }
      }
    }
  });

  return results;
}

/**
 * Counts the number of immutable links in the editor state.
 */
export function countImmutableLinks(state: EditorState): number {
  return findAllImmutableLinks(state).length;
}

/**
 * Removes duplicate immutable links from the editor state, keeping only the first one.
 * Returns the updated editor state, or the original if no duplicates found.
 */
export function removeDuplicateImmutableLinks(state: EditorState): EditorState {
  const linkLocations = findAllImmutableLinks(state);

  // If 0 or 1 links, nothing to remove
  if (linkLocations.length <= 1) {
    return state;
  }

  // Remove all but the first link (process in reverse order to preserve offsets)
  let updatedContent = state.getCurrentContent();
  for (let i = linkLocations.length - 1; i > 0; i--) {
    const loc = linkLocations[i];
    const selection = SelectionState.createEmpty(loc.blockKey).merge({
      anchorOffset: loc.start,
      focusOffset: loc.end,
    });

    // Remove the text and its entity
    updatedContent = Modifier.removeRange(
      updatedContent,
      selection,
      "backward"
    );
  }

  return EditorState.push(state, updatedContent, "remove-range");
}
